import type { FormAnswers, FormCondition, WebForm, WebFormField } from './types';

/**
 * The rules every renderer and every validator agree on. The server
 * (`apps/api/src/lib/mpForms.ts`) applies the same rules before it writes,
 * so a form that passes here is a form MP will accept.
 */

export const same = (a: string | null | undefined, b: string | null | undefined) =>
  (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();

/** Options can carry MP editor markup. Compare and display on the text. */
export function plain(text: string | null | undefined): string {
  if (!text) return '';
  return text
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();
}

export function childrenOf(form: WebForm, field: WebFormField): WebFormField[] {
  return form.fields.filter((f) => f.parentId === field.id && !f.hidden);
}

/** The options a field offers, after `optionsAllow` has had its say. */
export function offeredOptions(field: WebFormField): string[] {
  if (!field.optionsAllow?.length) return field.options;
  const allowed = field.options.filter((o) => field.optionsAllow!.includes(o));
  return allowed.length ? allowed : field.options;
}

const answerOf = (answers: FormAnswers, id: number) => (answers[String(id)] ?? '').trim();

function conditionHolds(c: FormCondition, answers: FormAnswers): boolean {
  const a = answerOf(answers, c.field);
  switch (c.op) {
    case 'answered':
      return a !== '';
    case 'not_answered':
      return a === '';
    case 'eq':
      return typeof c.value === 'string' && same(a, c.value);
    case 'ne':
      return typeof c.value === 'string' && !same(a, c.value);
    case 'in':
      return Array.isArray(c.value) && c.value.some((v) => same(v, a));
    default:
      return true;
  }
}

/**
 * Whether a field is on screen right now. Hidden fields never are; a child
 * follows its parent; MP's `Depends_On` wants one exact answer; our
 * `visible_when` wants its conditions to hold. A field with no rule, or a
 * rule that cannot be evaluated, is shown: an extra question asked is a
 * moment's confusion, a required question silently skipped is an answer
 * nobody knows is missing.
 */
export function isVisible(form: WebForm, field: WebFormField, answers: FormAnswers): boolean {
  if (field.hidden) return false;
  if (field.parentId !== null) {
    const parent = form.fields.find((f) => f.id === field.parentId);
    if (parent && !isVisible(form, parent, answers)) return false;
  }
  if (field.dependsOn !== null) {
    const gate = form.fields.find((f) => f.id === field.dependsOn);
    if (gate) {
      if (!isVisible(form, gate, answers)) return false;
      if (!same(plain(answerOf(answers, gate.id)), plain(field.dependsOnValue))) return false;
    }
  }
  const when = field.visibleWhen;
  if (when) {
    if (when.all?.length && !when.all.every((c) => conditionHolds(c, answers))) return false;
    if (when.any?.length && !when.any.some((c) => conditionHolds(c, answers))) return false;
  }
  return true;
}

/** A child of a group counts as answered when ticked, or when typed into. */
export function childAnswered(c: WebFormField, answers: FormAnswers): boolean {
  const v = answerOf(answers, c.id);
  return c.kind === 'checkbox' ? same(v, 'Yes') : v !== '';
}

/** The visible top-level fields still missing a required answer. */
export function missingRequired(form: WebForm, answers: FormAnswers): WebFormField[] {
  return form.fields.filter((f) => {
    if (!f.required || f.parentId !== null || f.kind === 'file') return false;
    if (!isVisible(form, f, answers)) return false;
    if (f.kind === 'instructions') {
      const kids = childrenOf(form, f);
      return kids.length > 0 && !kids.some((c) => childAnswered(c, answers));
    }
    if (f.kind === 'checkbox') return !same(answers[String(f.id)], 'Yes');
    return answerOf(answers, f.id) === '';
  });
}

/** Set one answer; an empty value removes the key so "unanswered" has one spelling. */
export function withAnswer(answers: FormAnswers, id: number, value: string): FormAnswers {
  const next = { ...answers };
  if (value) next[String(id)] = value;
  else delete next[String(id)];
  return next;
}

/**
 * A "please specify" child: a text box under a multiselect group, the way
 * Woodside's Connect Card asks "Other - please specify". Drawn as a follow-up
 * input beneath the chips rather than as a chip of its own.
 */
export function isFollowUpChild(c: WebFormField): boolean {
  return c.parentId !== null && c.kind !== 'checkbox';
}

/**
 * Whether a field is asking which campus somebody calls home, without anyone
 * having configured it. Seventeen of Woodside's forms carry the same
 * fourteen-campus dropdown labelled "Campus"; they should all get the campus
 * picker, and a `churchHub.component` of CampusPicker still wins.
 */
export function isCampusField(field: WebFormField, campusNames: string[]): boolean {
  if (field.component === 'CampusPicker') return true;
  if (field.component) return false;
  if (field.kind !== 'select' && field.kind !== 'radio') return false;
  if (!/campus/i.test(field.label)) return false;
  const names = new Set(campusNames.map((n) => n.toLowerCase()));
  const hits = field.options.filter((o) => names.has(plain(o).toLowerCase())).length;
  return hits >= 3;
}

/**
 * WHOSE QUESTION THIS IS, IN WORDS THAT FIT.
 * =========================================
 *
 * A question written for the person answering reads wrong when somebody is
 * registering their wife: "Where are you at with Woodside Students" should
 * become "Where is Sarah at". `title_about` is how a field says its other
 * phrasing, and `[Name]` is how it says where the name goes.
 *
 * PRONOUNS ARE THE OTHER HALF. "Would Sarah like to share their testimony?"
 * is understood but clumsy, and the church already knows the answer for most
 * people, so `[their]`, `[them]`, `[they]` and `[theirs]` resolve from the
 * gender MP holds.
 *
 * THEY/THEM IS THE DEFAULT, NOT A LAST RESORT. MP records Male and Female and
 * nothing else, and **7,134 of Woodside's 37,973 active contacts have no
 * gender at all** — one in five. A guess there is a wrong pronoun in front of
 * somebody's own family, which is worse than the neutral one that is never
 * wrong. The same reasoning the household list already uses when it prefers a
 * repeated first name to a pronoun.
 *
 * A NOTE ON VERBS, for whoever writes the next template. Singular they takes
 * a plural verb — "they are", not "they is" — so a template using `[they]` as
 * the subject cannot agree for all three cases. Write the name as the subject
 * ("Would [Name] like…") and keep the pronouns possessive, which is what
 * every question here does.
 */
const PRONOUNS = {
  male: { they: 'he', them: 'him', their: 'his', theirs: 'his' },
  female: { they: 'she', them: 'her', their: 'her', theirs: 'hers' },
  neutral: { they: 'they', them: 'them', their: 'their', theirs: 'theirs' },
} as const;

export type AboutGender = 'male' | 'female' | null | undefined;

/** Capitalised where the token was: `[Their]` gives "Her", `[their]` gives "her". */
export function personalise(template: string, name: string, gender: AboutGender): string {
  const set =
    gender === 'male' ? PRONOUNS.male : gender === 'female' ? PRONOUNS.female : PRONOUNS.neutral;
  return template.replace(/\[(Name|they|them|their|theirs)\]/gi, (whole, raw: string) => {
    const key = raw.toLowerCase() as 'name' | keyof typeof set;
    const word = key === 'name' ? name : set[key];
    if (!word) return whole;
    // The token's own first letter decides, so one template can open a
    // sentence with [Their] and carry [their] later in the same line.
    return /^[A-Z]/.test(raw) ? word.charAt(0).toUpperCase() + word.slice(1) : word;
  });
}
